(function(global) {
  'use strict';

  function jsonRequest(url, body, headers) {
    const controller = new global.AbortController();
    const timer = global.setTimeout(function() { controller.abort(); }, 30000);
    return fetch(url, {
      signal: controller.signal,
      method: 'POST',
      mode: 'cors',
      credentials: 'omit',
      cache: 'no-store',
      headers: Object.assign({ 'content-type': 'application/json' }, headers || {}),
      body: JSON.stringify(body || {})
    }).then(async function(response) {
      const value = await response.json().catch(function() { return {}; });
      if (!response.ok || value.ok !== true) {
        const error = new Error(value.message || 'The upload service could not complete this request.');
        error.code = value.error || value.code || '';
        error.httpStatus = response.status;
        throw error;
      }
      return value;
    }).catch(function(error) {
      if (error.name === 'AbortError') { const timeout = new Error('The upload service took too long to respond. Remove this file and select it again.'); timeout.code = 'TIMEOUT'; throw timeout; }
      throw error;
    }).finally(function() { global.clearTimeout(timer); });
  }

  function receiptIsCurrent(receipt) {
    try {
      const body = String(receipt || '').split('.')[0].replace(/-/g, '+').replace(/_/g, '/');
      const claims = JSON.parse(global.atob(body));
      return claims.kind === 'upload' && Number.isSafeInteger(claims.exp)
        && claims.exp * 1000 > Date.now() + 60000;
    } catch (_) {
      return false;
    }
  }

  function expireEntries(entries) {
    let expired = false;
    entries.forEach(function(entry) {
      if (entry.status === 'uploaded' && !receiptIsCurrent(entry.upload_receipt)) {
        entry.status = 'failed';
        entry.error_message = 'This saved upload has expired. Remove it and select the file again.';
        expired = true;
      }
    });
    return expired;
  }

  function hex(buffer) {
    return Array.from(new Uint8Array(buffer)).map(function(value) {
      return value.toString(16).padStart(2, '0');
    }).join('');
  }

  function guessedMimeType(file) {
    if (file.type) return file.type.toLowerCase();
    const extension = String(file.name || '').toLowerCase().split('.').pop();
    return ({
      jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', heic: 'image/heic', heif: 'image/heif',
      pdf: 'application/pdf', doc: 'application/msword',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      xls: 'application/vnd.ms-excel', xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      ppt: 'application/vnd.ms-powerpoint', pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      txt: 'text/plain'
    })[extension] || '';
  }

  function putFile(url, headers, file, onProgress, xhrs, ids) {
    return new Promise(function(resolve, reject) {
      const uploadIds = Array.isArray(ids) ? ids.filter(Boolean) : [ids].filter(Boolean);
      const xhr = new XMLHttpRequest();
      uploadIds.forEach(function(id) { xhrs.set(id, xhr); });
      function clearTrackedXhrs() {
        uploadIds.forEach(function(id) { xhrs.delete(id); });
      }
      xhr.open('PUT', url, true);
      xhr.timeout = 10 * 60 * 1000;
      xhr.addEventListener('timeout', function() { clearTrackedXhrs(); reject(Object.assign(new Error('The file upload took too long. Remove this file and select it again.'), {code:'TIMEOUT'})); });
      Object.keys(headers || {}).forEach(function(name) { xhr.setRequestHeader(name, headers[name]); });
      xhr.upload.addEventListener('progress', function(event) {
        if (event.lengthComputable) onProgress(Math.round(event.loaded / event.total * 100));
      });
      xhr.addEventListener('load', function() {
        clearTrackedXhrs();
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(Object.assign(new Error('The file upload was rejected. Please try again.'), {code:'UPLOAD_STORAGE_FAILED', httpStatus:xhr.status}));
      });
      xhr.addEventListener('error', function() { clearTrackedXhrs(); reject(new Error('The file upload was interrupted. Please try again.')); });
      xhr.addEventListener('abort', function() { clearTrackedXhrs(); reject(new Error('Upload canceled.')); });
      xhr.send(file);
    });
  }

  function createUploader(options) {
    const apiBase = String(options.apiBase || '').replace(/\/$/, '');
    const entries = [];
    const xhrs = new Map();
    const hashSlots = [Promise.resolve(), Promise.resolve()];
    let nextHashSlot = 0;

    function telemetry(name, data) { try { if (options.onTelemetry) options.onTelemetry(name, data); } catch (_) {} }
    function headers() { try { return options.requestHeaders ? options.requestHeaders(global.crypto.randomUUID()) : {}; } catch (_) { return {}; } }

    function assertNotCanceled(entry) {
      if (entry.canceled) throw new Error('Upload canceled.');
    }

    function hashFile(file, entry) {
      const slot = nextHashSlot;
      nextHashSlot = (nextHashSlot + 1) % hashSlots.length;
      // Keep large read buffers out of the rest of the upload's lifetime.
      const task = hashSlots[slot].then(async function() {
        assertNotCanceled(entry);
        const buffer = await file.arrayBuffer();
        assertNotCanceled(entry);
        const digest = await global.crypto.subtle.digest('SHA-256', buffer);
        assertNotCanceled(entry);
        return hex(digest);
      });
      hashSlots[slot] = task.catch(function() {});
      return task;
    }

    function changed() {
      options.onChange(entries.map(function(entry) { return Object.assign({}, entry); }));
    }

    async function addOne(file, position, metadata, batch) {
      const attemptId = global.crypto.randomUUID();
      const startedAt = Date.now();
      let stage = 'session';
      const localId = 'local-' + attemptId;
      telemetry('upload_started', { stage: stage, attemptId: attemptId });
      const entry = {
        id: localId,
        attachment_position: position,
        original_name: file.name,
        safe_name: file.name,
        mime_type: guessedMimeType(file),
        size_bytes: file.size,
        status: 'uploading',
        progress: 0,
        canceled: false
      };
      entries.push(entry);
      changed();
      try {
        assertNotCanceled(entry);
        // Share a session only within this selection, never across later file selections.
        if (!batch.session) batch.session = jsonRequest(apiBase + '/session', {}, headers());
        const activeSession = await batch.session;
        assertNotCanceled(entry);
        if (!entry.mime_type || activeSession.allowed_mime_types.indexOf(entry.mime_type) < 0) {
          throw Object.assign(new Error('This file type is not supported.'), {code:'FILE_TYPE'});
        }
        if (file.size < 1 || file.size > activeSession.max_file_bytes) {
          throw Object.assign(new Error('Each file must be 25 MB or smaller.'), {code:'FILE_SIZE'});
        }
        const sha256 = await hashFile(file, entry);
        stage = 'presign';
        const reserved = await jsonRequest(apiBase + '/uploads/presign', {
          session_token: activeSession.session_token,
          drawer_position: Number(metadata.drawer_position || 0),
          attachment_position: position,
          file_name: file.name,
          mime_type: entry.mime_type,
          byte_count: file.size,
          sha256: sha256
        }, headers());
        assertNotCanceled(entry);
        entry.upload_id = reserved.upload_id;
        entry.safe_name = reserved.safe_name;
        changed();
        stage = 'transfer';
        await putFile(reserved.upload_url, reserved.upload_headers, file, function(progress) {
          entry.progress = progress;
          changed();
        }, xhrs, entry.id);
        assertNotCanceled(entry);
        stage = 'confirm';
        const confirmed = await jsonRequest(apiBase + '/uploads/confirm', {
          session_token: activeSession.session_token,
          upload_id: reserved.upload_id
        }, headers());
        assertNotCanceled(entry);
        entry.upload_id = confirmed.upload_id;
        entry.upload_receipt = confirmed.upload_receipt;
        entry.sha256 = confirmed.sha256;
        entry.mime_type = confirmed.mime_type;
        entry.size_bytes = confirmed.size_bytes;
        entry.status = 'uploaded';
        telemetry('upload_completed', { stage: stage, attemptId: attemptId, durationMs: Date.now() - startedAt });
        entry.progress = 100;
        changed();
      } catch (error) {
        entry.status = entry.canceled ? 'canceled' : 'failed';
        telemetry(entry.canceled ? 'upload_cancelled' : 'upload_failed', { stage: stage, attemptId: attemptId, durationMs: Date.now() - startedAt, errorCode: entry.canceled ? 'CANCELLED' : error.code || 'NETWORK', httpStatus: error.httpStatus });
        entry.error_message = error && error.message || 'Upload failed. Remove and retry.';
        changed();
        if (entry.status !== 'canceled' && options.onError) options.onError(entry.error_message);
      }
    }

    return {
      addFiles: async function(fileList, context) {
        const files = Array.from(fileList || []);
        const current = entries.length;
        if (current + files.length > options.maxFiles) { telemetry('validation_failed', { stage:'form', errorCode:'TOO_MANY_FILES' }); throw new Error('Each drawer supports up to ' + options.maxFiles + ' files.'); }
        const usedPositions = new Set(entries.map(function(entry) { return entry.attachment_position; }));
        const batch = {};
        await Promise.all(files.map(function(file) {
          let position = 0;
          while (usedPositions.has(position)) position += 1;
          usedPositions.add(position);
          return addOne(file, position, {
            drawer_position: context && context.metadata && context.metadata.drawer_position || 0
          }, batch);
        }));
      },
      seedFiles: function(files) {
        entries.splice(0, entries.length);
        (files || []).forEach(function(file, index) {
          if (!file) return;
          const position = Number.isInteger(file.attachment_position) ? file.attachment_position : index;
          const ready = file.status === 'uploaded' && file.upload_id && file.upload_receipt;
          entries.push(Object.assign({}, file, {
            id: file.id || file.upload_id || 'local-' + global.crypto.randomUUID(),
            attachment_position: position,
            status: ready ? 'uploaded' : 'failed',
            progress: ready ? 100 : 0,
            error_message: ready ? '' : 'This file was not fully uploaded. Remove it and select it again.'
          }));
        });
        expireEntries(entries);
        changed();
      },
      validateForSubmission: function() {
        if (expireEntries(entries)) changed();
        return entries.every(function(entry) { return entry.status === 'uploaded'; });
      },
      cancelFile: function(id) {
        const entry = entries.find(function(candidate) { return candidate.id === id; });
        if (entry) {
          entry.canceled = true;
          entry.status = 'canceled';
          entry.error_message = 'Upload canceled.';
          changed();
        }
        const xhr = xhrs.get(id);
        if (xhr) xhr.abort();
      },
      removeFile: function(id) {
        const index = entries.findIndex(function(entry) { return entry.id === id; });
        if (index >= 0) {
          entries[index].canceled = true;
          const xhr = xhrs.get(id);
          if (xhr) xhr.abort();
          entries.splice(index, 1);
        }
        changed();
      },
      clear: function() {
        entries.forEach(function(entry) { entry.canceled = true; });
        xhrs.forEach(function(xhr) { xhr.abort(); });
        xhrs.clear();
        entries.splice(0, entries.length);
        changed();
      }
    };
  }

  global.NeatDesignRequestUploader = { createUploader: createUploader };
})(window);
